import { Location } from '@angular/common';
import { Component, computed, effect, inject, input, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { linkedQueryParam } from 'ngxtension/linked-query-param';
import { tryGetErrorMessage } from '../../../error.utils';
import { ManageShrinkPageComponent } from '../../../public/manage-shrink/manage-shrink.component';
import { StripeSubscriptionManagePageComponent } from '../../../public/stripe-subscription-manage-card/stripe-subscription-manage-page.component';
import { ArchiveService } from '../../../service/archive.service';
import { BranchService } from '../../../service/branch.service';
import { LocationGroupService } from '../../../service/location-group.service';
import { LocationService } from '../../../service/location.service';
import { NotifyService } from '../../../service/notify.service';
import { UserLoginService } from '../../../service/user-login.service';
import { ArchivedLocation, Branch, LocationCamera, LocationResponse } from '../../../types';
import { LocationGroup } from '../../../types/location-group-response';
import { BillingPortalBranchCardComponent } from '../../components/billing-portal-branch-card/billing-portal-branch-card.component';
import { AssetRecoveryComponent } from '../asset-recovery/asset-recovery.component';
import { BranchBillingComponent } from '../branch-billing/branch-billing.component';
import { CameraHealthComponent } from '../camera-health/camera-health.component';
import { IdentitiesComponent } from '../identities/identities.component';
import { InstallerDashboardComponent } from '../installer-dashboard/installer-dashboard.component';
import { LocationChangeHistoryComponent } from '../location-change-history/location-change-history.component';
import { LocationProfileComponent } from '../location-profile/location-profile.component';
import { MarketSnapshotDetailComponent } from '../market-snapshots/market-snapshot-detail/market-snapshot-detail.component';

@Component({
  selector: 'app-bl-management',
  templateUrl: './bl-management.component.html',
  styleUrls: ['./bl-management.component.scss'],
  standalone: false,
  providers: [],
})
export class BlManagementComponent implements OnInit {
  private userService = inject(UserLoginService);
  private branchService = inject(BranchService);
  private locationGroupService = inject(LocationGroupService);
  private locationService = inject(LocationService);
  private archiveService = inject(ArchiveService);
  private notifyService = inject(NotifyService);

  private router = inject(Router);
  private location = inject(Location);

  public branchId = input<string>('');
  public locationId = input<string>('');
  public locationCameraId = input<string>('');
  public locationGroupId = input<string>('');

  public allBranches = signal<Branch[]>([]);
  public allLocationsForBranch = signal<LocationResponse[]>([]);
  public allArchivedLocationsForBranch = signal<ArchivedLocation[]>([]);
  public allLocationGroups = signal<LocationGroup[]>([]);

  public selectedBranch = signal<Branch | undefined>(undefined);
  public selectedLocation = signal<LocationResponse | undefined>(undefined);
  public selectedLocationCamera = signal<LocationCamera | undefined>(undefined);
  public selectedLocationGroup = signal<LocationGroup | undefined>(undefined);

  public isLoadingBranches = signal<boolean>(false);
  public isLoadingLocations = signal<boolean>(false);
  public isLoadingLocationGroups = signal<boolean>(false);

  public subpage = linkedQueryParam<string>('subpage', {
    defaultValue: '',
  });

  public isSuspendedBillingOnly = computed(
    () =>
      !this.userService.currentUserIsAdmin() &&
      ((this.userService.currentProfile()?.access_suspended ?? false) || (this.selectedBranch()?.keystore?.suspend_access ?? false)),
  );

  constructor() {
    effect(() => {
      if (this.isSuspendedBillingOnly() && this.branchId()) {
        this.subpage.set('billing');
      }
    });
  }

  // 🙃
  // this should not be an any, this should also be a router
  // sub-routers in angular are the most finnicky thing I've ever used, so instead we do this
  // this will dynamically load components based on the subpage query param
  // also, there needs to be a unifying type for the component but this was a struggle so we're calling it good here
  public readonly allSubpages: Array<{ key: string; label: string; usesLocation?: boolean; component: any }> = [
    { key: 'billing', label: 'Billing', component: BillingPortalBranchCardComponent },
    { key: 'billing-history', label: 'Billing History', component: BranchBillingComponent },
    { key: 'camera-health', label: 'Camera Health', component: CameraHealthComponent },
    { key: 'subscriptions', label: 'Subscriptions', component: StripeSubscriptionManagePageComponent },
    { key: 'shrink', label: 'Shrink', component: ManageShrinkPageComponent },
    { key: 'installer-dashboard', label: 'Installer Dashboard', component: InstallerDashboardComponent },
    { key: 'identities', label: 'Identities', component: IdentitiesComponent },
    { key: 'profile', label: 'Profile', component: LocationProfileComponent, usesLocation: true },
    { key: 'change-history', label: 'Change History', component: LocationChangeHistoryComponent, usesLocation: true },
    { key: 'market-snapshot', label: 'Snapshot Detail', component: MarketSnapshotDetailComponent, usesLocation: true },
    { key: 'asset-recovery', label: 'Asset Recovery', component: AssetRecoveryComponent, usesLocation: true },
  ];

  public readonly allRootPages: Array<{ key: string; label: string }> = [
    { key: 'branch', label: 'Branches' },
    { key: 'locationgroup', label: 'Location Groups' },
  ];

  public currentRootPage = computed(() => this.allRootPages.find((p) => this.location.path().includes(p.key)) || this.allRootPages[0]);

  private currentSubpage = computed(() => this.allSubpages.find((s) => s.key === this.subpage()));
  public currentSubpageComponent = computed(() => this.currentSubpage()?.component);
  public currentSubpageComponentName = computed(() => this.currentSubpage()?.label);
  public currentSubpageUsesLocation = computed(() => this.currentSubpage()?.usesLocation);

  public subpageComponentInputs = computed(() => ({
    branchId: this.branchId(),
    branchData: this.selectedBranch(),
    ...(this.currentSubpageUsesLocation() ? { locationId: this.locationId(), locationData: this.selectedLocation() } : {}),
  }));

  public isLocationGroupGeneralManagement = computed(() => this.location.path().includes('/locationgroup') && !this.locationGroupId());
  public isLocationGroupSpecificManagement = computed(() => this.location.path().includes('/locationgroup') && this.locationGroupId());

  public isGeneralBranchManagement = computed(
    () => this.location.path().includes('/branch') && !this.branchId() && !this.locationId() && !this.location.path().includes('/location'),
  );

  public isSpecificBranchManagement = computed(
    () => this.location.path().includes('/branch') && this.branchId() && !this.locationId() && !this.location.path().includes('/location'),
  );

  public isGeneralLocationManagement = computed(
    () => this.location.path().includes('/branch') && this.branchId() && !this.locationId() && this.location.path().includes('/location'),
  );

  public isSpecificLocationManagement = computed(
    () =>
      this.location.path().includes('/branch') &&
      this.branchId() &&
      this.locationId() &&
      this.location.path().includes('/location') &&
      !this.location.path().includes('/camera'),
  );

  public isSpecificCameraManagement = computed(
    () =>
      this.location.path().includes('/branch') &&
      this.branchId() &&
      this.locationId() &&
      this.locationCameraId() &&
      this.location.path().includes('/camera'),
  );

  ngOnInit() {
    this.refreshBranches();
    this.refreshLocationGroups();
  }

  selectBranch(branch: Branch) {
    this.router.navigate(['/home', 'manage', 'branch', branch.id]);

    this.selectedBranch.set(branch);
  }

  selectLocation(location: LocationResponse) {
    this.router.navigate(['/home', 'manage', 'branch', this.branchId(), 'location', location.id]);

    this.selectedLocation.set(location);
  }

  selectLocationCamera(locationCamera: LocationCamera) {
    this.router.navigate([
      '/home',
      'manage',
      'branch',
      this.branchId(),
      'location',
      locationCamera.location_id,
      'camera',
      locationCamera.id,
    ]);

    this.selectedLocationCamera.set(locationCamera);
  }

  selectLocationGroup(locationGroup: LocationGroup) {
    this.router.navigate(['/home', 'manage', 'locationgroup', locationGroup.id]);

    this.selectedLocationGroup.set(locationGroup);
  }

  refreshBranches() {
    this.isLoadingBranches.set(true);

    this.branchService.getBranches().subscribe({
      next: (branches) => {
        this.allBranches.set(branches.items);

        if (this.branchId()) {
          this.selectedBranch.set(this.allBranches().find((b) => b.id.toString() === this.branchId().toString()));

          if (!this.isSuspendedBillingOnly()) {
            this.refreshLocations();
          }
        }
      },
      complete: () => {
        this.isLoadingBranches.set(false);
      },
    });
  }

  refreshLocationGroups() {
    this.isLoadingLocationGroups.set(true);

    this.locationGroupService.getAllLocationGroups().subscribe({
      next: (locationGroups) => {
        this.allLocationGroups.set(locationGroups.items);

        if (this.locationGroupId()) {
          this.selectedLocationGroup.set(this.allLocationGroups().find((lg) => lg.id.toString() === this.locationGroupId().toString()));
        }
      },
      complete: () => {
        this.isLoadingLocationGroups.set(false);
      },
    });
  }

  refreshLocations() {
    if (!this.branchId()) return;

    this.isLoadingLocations.set(true);

    this.allArchivedLocationsForBranch.set([]);
    this.allLocationsForBranch.set([]);

    this.locationService
      .getAllLocations({
        branch_ids: [+this.branchId()],
      })
      .subscribe({
        next: (response) => {
          this.allLocationsForBranch.set(response.items);

          if (this.locationId()) {
            this.selectedLocation.set(this.allLocationsForBranch().find((l) => l.id.toString() === this.locationId().toString()));
            this.refreshCamera();
          }

          this.loadArchivedLocations(+this.branchId());
        },
        error: (error) => {
          this.notifyService.notify('Failed to load branch locations: ' + tryGetErrorMessage(error));
        },
        complete: () => {
          this.isLoadingLocations.set(false);
        },
      });
  }

  private loadArchivedLocations(branchId: number) {
    if (!this.userService.currentUserIsAdmin()) return;

    this.archiveService.getMyArchivedLocations(branchId).subscribe(
      (locations) => {
        this.allArchivedLocationsForBranch.set(locations);
      },
      (error) => {
        this.notifyService.notify('Failed to load archived locations: ' + tryGetErrorMessage(error));
      },
    );
  }

  refreshCamera() {
    if (!this.locationId() || !this.locationCameraId()) return;

    this.locationService
      .getLocationCameraById({
        locationId: +this.locationId(),
        locationCameraId: this.locationCameraId(),
      })
      .subscribe({
        next: (camera) => {
          this.selectedLocationCamera.set(camera);
        },
        error: (error) => {
          this.notifyService.notify('Failed to load camera: ' + tryGetErrorMessage(error));
        },
      });
  }
  removeLocation(location: LocationResponse) {
    this.allLocationsForBranch.set(this.allLocationsForBranch().filter((l) => l.id !== location.id));
  }
}
